import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Pill, Plus, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function MAR() {
  const { user } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [individuals, setIndividuals] = useState([]);
  const [open, setOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(null);
  const [f, setF] = useState({
    individual_id:"", resident_name:"", medication:"", dosage:"", frequency:"BID",
    times:"08:00,20:00", start_date:new Date().toISOString().slice(0,10), requires_delegation:true,
  });
  const [logF, setLogF] = useState({ status:"administered", notes:"" });

  const load = async () => {
    const s = await api.get("/mar/schedules");
    setSchedules(s.data);
    const l = await api.get("/mar/logs");
    setLogs(l.data);
    // Sprint 3.5 (M3.5): load participants for the picker. Admin+RN roles
    // can see the full active roster; discharged/deceased/transferred are
    // filtered out client-side so users can't pick them in the first place.
    if (user.role === "admin" || user.role === "rn") {
      try {
        const inds = await api.get("/individuals", { params: { limit: 500 } });
        setIndividuals((inds.data.data || []).filter(
          (i) => i.status === "active" || i.status === "intake",
        ));
      } catch { /* individuals endpoint may 403 for non-admins */ }
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.individual_id) {
      toast.error("Select a participant before saving the schedule.");
      return;
    }
    try {
      await api.post("/mar/schedules", {
        ...f,
        times: f.times.split(",").map((x)=>x.trim()).filter(Boolean),
        start_date: new Date(f.start_date).toISOString(),
      });
      toast.success("Schedule added");
      setOpen(false);
      // Reset form so the next open starts clean
      setF({
        individual_id:"", resident_name:"", medication:"", dosage:"", frequency:"BID",
        times:"08:00,20:00", start_date:new Date().toISOString().slice(0,10), requires_delegation:true,
      });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const logAdmin = async (sched) => {
    try {
      await api.post("/mar/logs", {
        schedule_id: sched.id,
        scheduled_for: new Date().toISOString(),
        status: logF.status,
        notes: logF.notes,
      });
      toast.success("MAR logged");
      setLogOpen(null); setLogF({status:"administered", notes:""}); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="accp-label">Digital MAR</div>
          <h1 className="text-4xl font-semibold text-primary" style={{fontFamily:'Outfit'}}>Medication Administration</h1>
          <p className="text-muted-foreground mt-1">CMT required to log doses. All administrations audited.</p>
        </div>
        {(user.role==="admin"||user.role==="rn") && (
          <button className="accp-btn-primary" onClick={()=>setOpen(true)} data-testid="add-mar-btn">
            <Plus size={16}/> New schedule
          </button>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {schedules.map((s) => {
          const recent = logs.filter((l)=>l.schedule_id===s.id).slice(0,3);
          return (
            <div key={s.id} className="accp-card p-5" data-testid={`mar-${s.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="accp-label">{s.resident_name}</div>
                  <h3 className="text-xl font-semibold text-primary mt-1 flex items-center gap-2" style={{fontFamily:'Outfit'}}>
                    <Pill size={18}/> {s.medication}
                  </h3>
                  <div className="text-sm text-muted-foreground mt-0.5">{s.dosage} · {s.frequency}</div>
                </div>
                <div className="text-right text-xs text-muted-foreground">
                  {s.times.map((t)=><div key={t}>{t}</div>)}
                </div>
              </div>
              <button className="accp-btn-primary w-full mt-4 py-2.5" onClick={()=>setLogOpen(s)} data-testid={`log-mar-${s.id}`}>
                <CheckCircle2 size={14}/> Log administration
              </button>
              {recent.length>0 && (
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="accp-label mb-2">Recent</div>
                  <ul className="space-y-1 text-xs text-muted-foreground">
                    {recent.map((l)=>(
                      <li key={l.id} className="flex justify-between">
                        <span>{l.administered_by_name} — {l.status}</span>
                        <span>{new Date(l.administered_at).toLocaleString()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
        {schedules.length===0 && <div className="text-muted-foreground col-span-full">No MAR schedules.</div>}
      </div>

      {open && (
        <div className="accp-modal-shell" onClick={()=>setOpen(false)}>
          <form onClick={(e)=>e.stopPropagation()} onSubmit={submit} className="accp-modal-card space-y-4">
            <h3 className="text-xl font-semibold text-primary" style={{fontFamily:'Outfit'}}>New MAR schedule</h3>
            <label><span className="accp-label">Participant *</span>
              <select required className="accp-input mt-1"
                value={f.individual_id}
                onChange={(e) => {
                  const id = e.target.value;
                  const ind = individuals.find((x) => x.id === id);
                  setF({
                    ...f,
                    individual_id: id,
                    // resident_name is kept as a display snapshot per ADR-0012;
                    // authoritative reference is individual_id.
                    resident_name: ind
                      ? `${ind.last_name}, ${ind.first_name}` : "",
                  });
                }}
                data-testid="mar-individual-picker">
                <option value="">Select participant…</option>
                {individuals.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.code} — {i.last_name}, {i.first_name}
                  </option>
                ))}
              </select>
              <div className="text-[11px] text-muted-foreground mt-1">
                Only active participants are shown. Discharged/deceased individuals
                cannot receive new medication schedules.
              </div>
            </label>
            <label><span className="accp-label">Medication</span>
              <input required className="accp-input mt-1" value={f.medication} onChange={(e)=>setF({...f, medication:e.target.value})}/>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label><span className="accp-label">Dosage</span>
                <input required className="accp-input mt-1" value={f.dosage} onChange={(e)=>setF({...f, dosage:e.target.value})}/>
              </label>
              <label><span className="accp-label">Frequency</span>
                <select className="accp-input mt-1" value={f.frequency} onChange={(e)=>setF({...f, frequency:e.target.value})}>
                  <option>QD</option><option>BID</option><option>TID</option><option>QID</option><option>PRN</option>
                </select>
              </label>
            </div>
            <label><span className="accp-label">Times (comma-separated, HH:MM)</span>
              <input className="accp-input mt-1" value={f.times} onChange={(e)=>setF({...f, times:e.target.value})}/>
            </label>
            <label><span className="accp-label">Start date</span>
              <input type="date" required className="accp-input mt-1" value={f.start_date} onChange={(e)=>setF({...f, start_date:e.target.value})}/>
            </label>
            <button type="submit" className="accp-btn-primary w-full">Save schedule</button>
          </form>
        </div>
      )}

      {logOpen && (
        <div className="accp-modal-shell" onClick={()=>setLogOpen(null)}>
          <div onClick={(e)=>e.stopPropagation()} className="accp-modal-card space-y-4">
            <div className="flex justify-between items-start">
              <h3 className="text-xl font-semibold text-primary" style={{fontFamily:'Outfit'}}>Log administration</h3>
              <button onClick={()=>setLogOpen(null)}><X size={18}/></button>
            </div>
            <div className="text-sm text-muted-foreground">
              {logOpen.medication} · {logOpen.dosage} — {logOpen.resident_name}
            </div>
            <label><span className="accp-label">Status</span>
              <select className="accp-input mt-1" value={logF.status} onChange={(e)=>setLogF({...logF, status:e.target.value})}>
                <option value="administered">Administered</option>
                <option value="refused">Refused</option>
                <option value="missed">Missed</option>
                <option value="hold">Held</option>
              </select>
            </label>
            <label><span className="accp-label">Notes</span>
              <textarea rows={3} className="accp-input mt-1" value={logF.notes} onChange={(e)=>setLogF({...logF, notes:e.target.value})}/>
            </label>
            <button onClick={()=>logAdmin(logOpen)} className="accp-btn-primary w-full" data-testid="mar-log-submit">Log</button>
          </div>
        </div>
      )}
    </div>
  );
}
